'use client';

import { c as _c } from "react/compiler-runtime";
import { DonationIntervals, PaymentMethods } from 'api-graphql-types/enums';
import { toLowerCase } from 'betterplace-utils';
import { Fieldset, FieldsetProps, Heading, LoadingSkeleton, RadioButton, Tooltip } from 'design-system/client';
import { useTranslations } from 'next-intl';
import { memo, useEffect, useState } from 'react';
import styles from './PaymentMethodSelection.module.css';
import useTrackPaymentMethodChange from './useTrackPaymentMethodChange';
import DonationFormField from '@/donationPages/_dependencies/components/DonationFormField';
import DirectDepositInfo from '@/donationPages/_dependencies/components/formParts/DirectDepositInfo';
import FiscalYearChangeNotice from '@/donationPages/_dependencies/components/formParts/FiscalYearChangeNotice';
import { useDonationFormValues } from '@/donationPages/_dependencies/helpers';
import { usePaymentMethodsToRender, useSelectDefaultPaymentMethod, type PaymentMethodsOrPlaceholder } from '@/donationPages/_dependencies/NativePaymentMethods';
import PaymentMethodIcon from '@/components/PaymentMethodIcon/PaymentMethodIcon';
type PaymentMethodProps = {
  name: PaymentMethods;
  isRecurring: boolean;
  isTwitterBrowser: boolean;
  nativePaymentsLoading: boolean;
  recurringPaymentMethodsToRender: PaymentMethodsOrPlaceholder[];
};
function PaymentMethod_(t0) {
  const $ = _c(30);
  const {
    name,
    isRecurring,
    isTwitterBrowser,
    nativePaymentsLoading,
    recurringPaymentMethodsToRender
  } = t0;
  const t = useTranslations();
  const isBrowserUnsupported = name === PaymentMethods.Paypal && isTwitterBrowser;
  let t1;
  if ($[0] !== isRecurring || $[1] !== name || $[2] !== recurringPaymentMethodsToRender) {
    t1 = isRecurring && !recurringPaymentMethodsToRender.includes(name);
    $[0] = isRecurring;
    $[1] = name;
    $[2] = recurringPaymentMethodsToRender;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isRecurringUnsupported = t1;
  const disabled = isBrowserUnsupported || isRecurringUnsupported;
  let t2;
  if ($[4] !== isBrowserUnsupported || $[5] !== isRecurringUnsupported || $[6] !== t) {
    t2 = isBrowserUnsupported ? t("nextjs.donate.errors.paypal_unsupported_browser") : isRecurringUnsupported ? t("nextjs.donate.recurring_disabled_tooltip") : null;
    $[4] = isBrowserUnsupported;
    $[5] = isRecurringUnsupported;
    $[6] = t;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const tooltipContent = t2;
  const t3 = !disabled;
  const t4 = disabled || nativePaymentsLoading;
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      container: styles.radio,
      label: styles.radioLabel
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== name) {
    t6 = toLowerCase(name);
    $[9] = name;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = `nextjs.donate.payment_method.labels.${t6}`;
  let t8;
  if ($[11] !== t || $[12] !== t7) {
    t8 = t(t7);
    $[11] = t;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== name) {
    t9 = <PaymentMethodIcon name={name} />;
    $[14] = name;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t8 || $[17] !== t9) {
    t10 = <div className={styles.labelContent}>{t8}{t9}</div>;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== name || $[20] !== t10 || $[21] !== t4) {
    t11 = <DonationFormField name="payment_method" type="radio" as={RadioButton} value={name} disabled={t4} classNames={t5}>{t10}</DonationFormField>;
    $[19] = name;
    $[20] = t10;
    $[21] = t4;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t11 || $[24] !== t3 || $[25] !== tooltipContent) {
    t12 = <Tooltip content={tooltipContent} inactive={t3}>{t11}</Tooltip>;
    $[23] = t11;
    $[24] = t3;
    $[25] = tooltipContent;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== name || $[28] !== t12) {
    t13 = <div key={name}>{t12}</div>;
    $[27] = name;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
const PaymentMethodItem = memo(PaymentMethod_);
type PaymentMethodsProps = {
  nativePaymentsLoading: boolean;
  paymentMethodsToRender: PaymentMethodsOrPlaceholder[];
  recurringPaymentMethodsToRender: PaymentMethodsOrPlaceholder[];
  isRecurring: boolean;
  isTwitterBrowser: boolean;
};
function PaymentMethods_({
  nativePaymentsLoading,
  paymentMethodsToRender,
  recurringPaymentMethodsToRender,
  isRecurring,
  isTwitterBrowser
}: PaymentMethodsProps) {
  return paymentMethodsToRender.map(name => {
    if (name === 'placeholder') return <div key="fake" className={styles.skeletonContainer}>
          <LoadingSkeleton width="100%" height="2.5rem" key="fake" />
        </div>;
    return <PaymentMethodItem key={name} name={name} isRecurring={isRecurring} isTwitterBrowser={isTwitterBrowser} nativePaymentsLoading={nativePaymentsLoading} recurringPaymentMethodsToRender={recurringPaymentMethodsToRender} />;
  });
}
const PaymentMethodList = memo(PaymentMethods_);
function PaymentMethodSelection(t0) {
  const $ = _c(27);
  const {
    className,
    layout: t1
  } = t0;
  const layout = t1 === undefined ? "vertical" : t1;
  const t = useTranslations();
  const {
    nativePaymentsLoading,
    paymentMethodsToRender,
    recurringPaymentMethodsToRender
  } = usePaymentMethodsToRender();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["interval", "payment_method"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [interval, paymentMethod] = useDonationFormValues(t2);
  const isRecurring = interval !== DonationIntervals.Single;
  const [isTwitterBrowser, setIsTwitterBrowser] = useState(false);
  useSelectDefaultPaymentMethod();
  let t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (typeof navigator === "undefined") {
        return;
      }
      const timeout = setTimeout(() => setIsTwitterBrowser(/Twitter/i.test(navigator.userAgent)), 0);
      return () => clearTimeout(timeout);
    };
    t4 = [];
    $[1] = t3;
    $[2] = t4;
  } else {
    t3 = $[1];
    t4 = $[2];
  }
  useEffect(t3, t4);
  useTrackPaymentMethodChange();
  let t5;
  if ($[3] !== t) {
    t5 = t("nextjs.donate.payment_method.headline");
    $[3] = t;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = <Heading level="heading-xs" className={styles.headingMargin}>{t5}</Heading>;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== className) {
    t7 = {
      className
    };
    $[7] = className;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isRecurring || $[10] !== isTwitterBrowser || $[11] !== nativePaymentsLoading || $[12] !== paymentMethodsToRender || $[13] !== recurringPaymentMethodsToRender) {
    t8 = <PaymentMethodList nativePaymentsLoading={nativePaymentsLoading} paymentMethodsToRender={paymentMethodsToRender} recurringPaymentMethodsToRender={recurringPaymentMethodsToRender} isRecurring={isRecurring} isTwitterBrowser={isTwitterBrowser} />;
    $[9] = isRecurring;
    $[10] = isTwitterBrowser;
    $[11] = nativePaymentsLoading;
    $[12] = paymentMethodsToRender;
    $[13] = recurringPaymentMethodsToRender;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== layout || $[16] !== t6 || $[17] !== t7 || $[18] !== t8) {
    t9 = <Fieldset legend={t6} layout={layout} layoutProps={t7} className={styles.fieldset}>{t8}</Fieldset>;
    $[15] = layout;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  let t11;
  if ($[20] !== paymentMethod) {
    t10 = <DirectDepositInfo paymentMethod={paymentMethod} />;
    t11 = <FiscalYearChangeNotice paymentMethod={paymentMethod} />;
    $[20] = paymentMethod;
    $[21] = t10;
    $[22] = t11;
  } else {
    t10 = $[21];
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t9) {
    t12 = <>{t9}{t10}{t11}</>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
export default memo(PaymentMethodSelection);